import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import { AcceptTermsPage } from "../features/auth/AcceptTermsPage";
import { ErrorState } from "../components/ui/ErrorState";
import { Loading } from "../components/ui/Page";
import { useSession } from "./session";

function Centered({ children }: { children: ReactNode }) {
  return <div className="mx-auto flex min-h-dvh max-w-xl flex-col justify-center gap-4 px-6">{children}</div>;
}

/** Signed-in screens. Anyone else goes to sign in and comes back here afterwards. */
export function RequireSession({ children }: { children?: ReactNode }) {
  const { state, refresh } = useSession();
  const loc = useLocation();
  if (state.status === "loading") return <Centered><Loading /></Centered>;
  if (state.status === "error") return <Centered><ErrorState error={state.error} onRetry={() => void refresh()} /></Centered>;
  if (state.status === "signed-out") {
    const next = loc.pathname + loc.search;
    return <Navigate to={`/sign-in?next=${encodeURIComponent(next)}`} replace />;
  }
  return children ?? <Outlet />;
}

/** Inside RequireSession: the app opens only once the Terms in force have been accepted. */
export function RequireTerms() {
  const { state } = useSession();
  if (state.status === "signed-in" && !state.me.terms.accepted) return <AcceptTermsPage />;
  return <Outlet />;
}

/** Only allows same-site paths as a post-sign-in target, never another origin. */
export function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/streams";
}

/** Sign-in and sign-up screens; a signed-in visitor goes straight on. */
export function GuestOnly() {
  const { state } = useSession();
  const [params] = useSearchParams();
  if (state.status === "loading") return <Centered><Loading /></Centered>;
  if (state.status === "signed-in") return <Navigate to={safeNext(params.get("next"))} replace />;
  return <Outlet />;
}
